import ApiClient from './ApiClient'

// Calls FitOn's own backend (api.fitonapp.com) directly — the same /user and
// /user/login endpoints fiton-web's app_fitonapp/apis/Auth.js already uses
// (signupPromise / loginPromise). fiton-care-web is a second frontend client
// of these, per docs/fiton-care-web-native-signup-login-plan.md (Step 1).
const apiClient = new ApiClient()

export const fitonSignupPromise = ({ name, email, password }) => {
  return apiClient
    .post('/user', {
      isFitOnAPI: true,
      body: {
        name,
        email,
        password,
        deviceId: 'web',
        // On 409 (email already registered) the backend would otherwise send
        // its own fiton-web magic-link email; useFitonAuth's
        // recoverExistingAccount already sends the Care one, so the user got
        // two (CARE-172, API-1132).
        skipMagicLinkEmail: true,
      },
    })
    .then((resp) => Promise.resolve(resp))
}

// Mirrors fiton-web's loginPromise response shape exactly: on success this
// resolves the *unwrapped* user object (`{ token, id, email, ... }`), on
// failure it resolves the raw `{ code, msg }` response — callers should
// check `.token` to tell the two apart, same as fiton-web does.
export const fitonLoginPromise = ({ email, password }) => {
  return apiClient
    .post('/user/login', {
      isFitOnAPI: true,
      body: {
        email,
        password,
        deviceId: 'web',
      },
    })
    .then((resp) => {
      const data = resp?.data || {}
      if (!data.token) {
        return Promise.resolve(resp)
      }
      return Promise.resolve({ ...data })
    })
}

// Mirrors fiton-web's checkUserExistPromise (app_fitonapp/apis/Auth.js):
// resolves a plain boolean. Used to branch a failed login into "send magic
// link + /checkemail" vs "just show the error", same as loginV2.js:1056-1071.
export const fitonCheckUserExistPromise = ({ email }) => {
  return apiClient
    .post('/user/exist', {
      isFitOnAPI: true,
      body: { email },
    })
    .then((resp) => Promise.resolve(!!resp?.data?.exist))
}

// Mirrors fiton-web's emailMagicLinkPromise (app_fitonapp/apis/Auth.js).
// NOTE: distinct from FitOnCareAPI.js's emailMagicLinkPromise — this one hits
// FitOn's own backend (/util/get_magic_link), whose email contains both a
// magic link and a 4-digit code. `domain_url` (API-852) makes the emailed
// link land back on Care's own magic-link landing page —
// <origin>/checkemail/<variant>?r=magiclink/<code> — instead of fiton-web;
// built here (rather than by each caller) so every send stays consistent
// with the /checkemail/:variant? route that consumes it.
//
// The base is hardcoded per environment (not window.location.origin): the
// backend only supports the real Care domains, so localhost dev also gets a
// staging link — grab the ?r=magiclink/<code> from the email and open it
// against localhost manually when testing.
export const fitonEmailMagicLinkPromise = ({ email, variant }) => {
  const domainBase =
    process.env.REACT_APP_ENV === 'staging'
      ? 'https://web-staging.fitoncare.com'
      : 'https://web.fitoncare.com'
  const domainUrl = `${domainBase}/checkemail${variant ? `/${variant}` : ''}`
  return apiClient
    .post('/util/get_magic_link', {
      isFitOnAPI: true,
      body: { email, domain_url: domainUrl },
      // BE need this version. We can remove it once care-app reached
      // version 3.8.0
      bypassVersion: '4.0.0',
    })
    .then((resp) => Promise.resolve(resp))
}

// Mirrors fiton-web's verifyMagicLinkByDigitCodePromise
// (app_fitonapp/apis/Auth.js): exchanges the emailed 4-digit code for a full
// user object ({ token, id, email, ... }), completing login without the user
// ever following the email's fiton-web link.
export const fitonVerifyMagicLinkByDigitCodePromise = ({
  digitCode,
  email,
}) => {
  return apiClient
    .post('/util/verify/magic-link/digit-code', {
      isFitOnAPI: true,
      body: { digitCode, email },
    })
    .then((resp) => Promise.resolve(resp))
}

// Mirrors fiton-web's verifyMagicLinkPromise (app_fitonapp/apis/Auth.js):
// exchanges the emailed magic-link code (the `magiclink/<code>` part of the
// email URL's `?r=` param) for the same full user object the digit-code
// endpoint returns ({ token, id, email, ... }). Used when the magic-link
// email lands on /checkemail/:variant (plan Step 7).
export const fitonVerifyMagicLinkPromise = ({ code }) => {
  return apiClient
    .post('/util/verify_magic_link', {
      isFitOnAPI: true,
      body: { code },
    })
    .then((resp) => Promise.resolve(resp))
}

// Mirrors fiton-web's getProfilePromise (app_fitonapp/apis/Auth.js): fetches the
// current FitOn profile so About You can prefill fields the user already has on
// file — the name typed at signup (until firstName/lastName are set), or, for a
// returning user hitting /login, whatever profile they'd already filled in.
export const fitonGetProfilePromise = () => {
  return apiClient
    .get('/user', { isFitOnAPI: true })
    .then((resp) => Promise.resolve(resp))
}

// Mirrors fiton-web's uploadProfilePromise (app_fitonapp/apis/Auth.js): a
// thin PUT /user passthrough — whatever fields the caller sends become the
// update (gender, genderOther, height, heightUnit, weight, weightUnit,
// birthday, firstName, lastName). Used by the About You step (Step 2).
export const fitonUpdateProfilePromise = (body) => {
  return apiClient
    .put('/user', {
      isFitOnAPI: true,
      body,
    })
    .then((resp) => Promise.resolve(resp))
}

// Mirrors fiton-web's getGoalPromise (app_fitonapp/apis/Auth.js): a thin
// GET /user/goal passthrough, minus fiton-web's 2s response cache and
// Braze/Amplitude profile syncing. Used by About You (CARE-137) to check
// whether the user already has a goal before seeding BMI-derived defaults —
// a present `goalName` (fiton-web's own signal, see Onboarding/index.js's
// formatDefaultGoal/uploadInfo) means don't overwrite it.
export const fitonGetGoalPromise = () => {
  return apiClient
    .get('/user/goal', { isFitOnAPI: true })
    .then((resp) => Promise.resolve(resp))
}

// Mirrors fiton-web's updateGoalPromise (app_fitonapp/apis/Auth.js): a thin
// PUT /user/goal passthrough (goalName, goalNumber, goalUnit, ...), minus
// fiton-web's Braze/Amplitude profile syncing, which fiton-care-web doesn't
// do. Used by About You (CARE-137) to seed the CMS Access flow's default
// fitness goal / target weight from BMI.
export const fitonUpdateGoalPromise = (body) => {
  return apiClient
    .put('/user/goal', {
      isFitOnAPI: true,
      body,
    })
    .then((resp) => Promise.resolve(resp))
}
